import { useCallback, useEffect, useState } from 'react';
import {
  radarOutlook,
  rainOutlook,
  WET_MM,
  type PrecipBucketDto,
  type RadarOutlook,
  type RainOutlook,
  type WeatherResponse,
} from '@familyhub/shared';
import { api } from '../api/client';
import { useRefreshOnReturn } from '../useRefreshOnReturn';
import { DropIcon, SunsetIcon, WeatherIcon, weatherLabel } from './codes';

/**
 * The weather on the dashboard, above the module tiles.
 *
 * Online-only, like everything that asks a live question — but it is the first
 * thing on the home screen, so it must never look broken. The last good answer
 * is held in memory across re-mounts (navigating away and back), a fresh fetch
 * replaces it, and a failure with nothing cached shows one quiet line rather
 * than an empty box. It refreshes on the same triggers the sync loop uses, so
 * a phone resumed after hours does not show yesterday's sky.
 */

/** Survives re-mounts within a session; the dashboard remounts on every return to it. */
let cached: WeatherResponse | null = null;

function round(n: number): number {
  return Math.round(n);
}

function mm(n: number): string {
  return `${n.toFixed(1).replace(/\.0$/, '')} mm`;
}

/**
 * The coming-hours strip shows the forecast **amount**, not the probability.
 * Open-Meteo reports 96% on hours it expects 0.0 mm for, so a row of near-
 * identical percentages looked like constant rain and flatly contradicted the
 * "rain from 17:00" summary beside it. The amount is the same fact the summary
 * and the daily figures use, so the whole card now tells one story: the droplet
 * fades with how much is coming, a dry hour barely there.
 */
function dropOpacity(precip: number): number {
  return precip < WET_MM ? 0.12 : Math.max(0.4, Math.min(1, precip / 2));
}

const capitalize = (s: string): string => s.charAt(0).toUpperCase() + s.slice(1);

/** How many of the coming hours the rain strip shows. */
const HOURS_SHOWN = 6;

/** "Nu" for the current hour, else "15u". */
function hourLabel(time: string, isFirst: boolean): string {
  return isFirst ? 'Nu' : `${time.slice(11, 13)}u`;
}

/** The one-line rain answer, in Dutch — the display side of the shared `rainOutlook`. */
function rainText(o: RainOutlook): string {
  if (o.state === 'raining') return 'Het regent nu';
  if (o.state === 'starts') return `Regen vanaf ${o.time.slice(11, 16)} (${o.probability}%)`;
  return 'Droog de komende uren';
}

/** The household's zone. Radar instants are absolute UTC, so their HH:mm is formatted here. */
const HOME_TZ = 'Europe/Amsterdam';

/**
 * `HH:mm` in the household's zone for an **absolute** ISO instant. The radar
 * buckets are UTC (unlike Open-Meteo's local wall-clock hours, whose HH:mm is a
 * plain slice), so slicing them would show the wrong clock — format instead.
 */
function hhmmLocal(iso: string): string {
  return new Intl.DateTimeFormat('nl-NL', {
    timeZone: HOME_TZ,
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(iso));
}

/** The one-line rain answer from the radar nowcast — no probability, radar has none. */
function radarText(o: RadarOutlook): string {
  if (o.state === 'raining') return 'Het regent nu';
  if (o.state === 'starts') return `Regen vanaf ${hhmmLocal(o.time)}`;
  return 'Droog de komende uren';
}

/** A rendered strip cell — the same shape whichever source fills it, so the JSX is one loop. */
interface StripCell {
  key: string;
  label: string;
  opacity: number;
  amount: string;
}

/** The mm/h at or above which a radar cell reads as wet — the radar's own bar (`getRainStart`). */
const RADAR_WET_MMH = 0.1;
/** 5-minute radar buckets per strip cell: 4 × 5 min = 20 min, so six cells span the ~2h nowcast. */
const RADAR_CELL_STEP = 4;

/** A radar cell fades with intensity (mm/h), a dry cell barely there — like the hourly strip. */
function radarDropOpacity(mmh: number): number {
  return mmh < RADAR_WET_MMH ? 0.12 : Math.max(0.4, Math.min(1, mmh / 2));
}

/** Rain **intensity**, mm/h — the radar's unit, distinct from the hourly strip's mm total. */
function mmhLabel(n: number): string {
  return `${n.toFixed(1).replace(/\.0$/, '')} mm/u`;
}

/** The coming-hours strip from Open-Meteo's hourly forecast (the fallback when radar is absent). */
function modelCells(hours: WeatherResponse['hourly']): StripCell[] {
  return hours.slice(0, HOURS_SHOWN).map((hour, i) => ({
    key: hour.time,
    label: hourLabel(hour.time, i === 0),
    opacity: dropOpacity(hour.precipitation),
    amount: hour.precipitation >= WET_MM ? mm(hour.precipitation) : '–',
  }));
}

/**
 * The coming-2h strip from the radar nowcast: 5-minute buckets folded into
 * 20-minute cells (the peak intensity in each), anchored to the slot covering
 * now. Shorter horizon than the model strip but honest about the light showers
 * the model flattens — the whole point of preferring radar for the near term.
 */
function radarCells(buckets: PrecipBucketDto[], nowMs: number): StripCell[] {
  let from = 0;
  for (let i = 0; i < buckets.length; i++) {
    if (Date.parse(buckets[i]!.time) <= nowMs) from = i;
    else break;
  }
  const cells: StripCell[] = [];
  for (let c = 0; c < HOURS_SHOWN; c++) {
    const slice = buckets.slice(from + c * RADAR_CELL_STEP, from + (c + 1) * RADAR_CELL_STEP);
    if (slice.length === 0) break;
    const peak = Math.max(...slice.map((b) => b.precipitation));
    cells.push({
      key: slice[0]!.time,
      label: c === 0 ? 'Nu' : hhmmLocal(slice[0]!.time),
      opacity: radarDropOpacity(peak),
      amount: peak >= RADAR_WET_MMH ? mmhLabel(peak) : '–',
    });
  }
  return cells;
}

/** Local calendar date as `YYYY-MM-DD` — the shape Open-Meteo stamps each day with. */
function localDateKey(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/**
 * Today's sunset as `HH:mm`, or null. Matched by date rather than assuming
 * `days[0]` is today, so a 15-minute cache spanning midnight cannot label
 * yesterday's sunset as tonight's; it falls back to `days[0]` if none matches.
 */
function sunsetToday(days: WeatherResponse['days']): string | null {
  if (days.length === 0) return null;
  const day = days.find((d) => d.date === localDateKey(new Date())) ?? days[0]!;
  return day.sunset ? day.sunset.slice(11, 16) : null;
}

/** "Vandaag" / "Morgen" / a weekday, from the day's own date — robust to a stale cache. */
function dayLabel(dateStr: string): string {
  const d = new Date(`${dateStr}T00:00:00`);
  if (Number.isNaN(d.getTime())) return dateStr;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diff = Math.round((d.getTime() - today.getTime()) / 86_400_000);
  if (diff === 0) return 'Vandaag';
  if (diff === 1) return 'Morgen';
  return capitalize(d.toLocaleDateString('nl-NL', { weekday: 'short' }));
}

export function WeatherCard() {
  const [data, setData] = useState<WeatherResponse | null>(cached);
  const [failed, setFailed] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const res = await api<WeatherResponse>('/api/v1/weather');
      cached = res;
      setData(res);
      setFailed(false);
    } catch {
      // Keep showing the last answer if we have one; only admit a problem with nothing to show.
      if (!cached) setFailed(true);
    }
  }, []);

  useRefreshOnReturn(refresh);
  // Fetch once on mount too — useRefreshOnReturn only fires on resume/online.
  useEffect(() => {
    void refresh();
  }, [refresh]);

  if (!data) {
    if (failed) {
      return <p className="weather weather-quiet">Weer niet beschikbaar</p>;
    }
    return <div className="weather weather-skeleton" aria-hidden />;
  }

  // The current conditions, from the current code — never days[0], which is a
  // whole-day aggregate and shows "rain" on a dry evening that rained earlier.
  const nowCode = data.current.weather_code;
  const hours = data.hourly ?? [];
  const sunset = sunsetToday(data.days);

  // The near-term rain answer prefers the Buienradar radar nowcast (the source
  // the push alert trusts): the model reports light, scattered showers as a flat
  // 0.0 mm and reads "droog" while it is raining. Radar is best-effort, so when
  // the server hands back none we fall back to the Open-Meteo hourly outlook.
  const radar = data.radar ?? [];
  const nowMs = Date.now();
  const near: RadarOutlook | RainOutlook =
    radar.length > 0 ? radarOutlook(radar, nowMs) : rainOutlook(data.current.precipitation, hours);
  const rainLine = radar.length > 0 ? radarText(near as RadarOutlook) : rainText(near as RainOutlook);
  const cells = radar.length > 0 ? radarCells(radar, nowMs) : modelCells(hours);
  const stripLabel = radar.length > 0 ? 'Neerslag komende 2 uur (radar)' : 'Neerslag komende uren';

  return (
    <section className="weather" aria-label="Weer">
      <div className="weather-now">
        <span className="weather-now-icon">
          <WeatherIcon code={nowCode} />
        </span>
        <span className="weather-now-temp">{round(data.current.temperature)}°</span>
        <span className="weather-now-meta">
          <span className="weather-now-desc">{weatherLabel(nowCode)}</span>
          <span className="weather-now-sub">Voelt als {round(data.current.apparent_temperature)}°</span>
          <span className="weather-now-stats">
            Wind {round(data.current.wind_speed)} km/u · Vocht {round(data.current.humidity)}%
          </span>
          {sunset && (
            <span className="weather-now-sun">
              <span className="weather-now-sun-icon" aria-hidden>
                <SunsetIcon />
              </span>
              Zon onder om {sunset}
            </span>
          )}
        </span>
      </div>

      {(hours.length > 0 || radar.length > 0) && (
        <>
          <p className="weather-rain" data-dry={near.state === 'dry'}>
            <span className="weather-rain-icon" aria-hidden>
              <DropIcon />
            </span>
            {rainLine}
          </p>

          {cells.length > 0 && (
            <ol className="weather-hours" aria-label={stripLabel}>
              {cells.map((cell) => (
                <li key={cell.key} className="weather-hour">
                  <span className="weather-hour-label">{cell.label}</span>
                  <span className="weather-hour-drop" style={{ opacity: cell.opacity }} aria-hidden>
                    <DropIcon />
                  </span>
                  <span className="weather-hour-amt">{cell.amount}</span>
                </li>
              ))}
            </ol>
          )}
        </>
      )}

      <ol className="weather-days">
        {data.days.map((day) => (
          <li key={day.date} className="weather-day">
            <span className="weather-day-label">{dayLabel(day.date)}</span>
            <span className="weather-day-icon">
              <WeatherIcon code={day.weather_code} />
            </span>
            <span className="weather-day-temps">
              <b>{round(day.temp_max)}°</b>
              <span className="weather-day-min">{round(day.temp_min)}°</span>
            </span>
            <span className="weather-day-precip">
              {day.precipitation_sum > 0 ? mm(day.precipitation_sum) : ''}
            </span>
          </li>
        ))}
      </ol>
    </section>
  );
}
